import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, AlertTriangle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useListing, ListingShell, FilterSelect } from "../components/Listing";
import { PageHeader, Loading, ErrorBox, SampleBadge, SaveButton, ReportButton, Badge, btn } from "../components/kit";
import { GovCard } from "../components/cards";
import { OtherOfficialSources } from "../components/OtherOfficialSources";
import { DetailSection } from "./JobDetail";
import { useFetch, label, fmtDate } from "../lib/api";

export const STATES = ["Andhra Pradesh", "Assam", "Bihar", "Delhi", "Gujarat", "Haryana", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh", "West Bengal"];
const KINDS = [["job", "Government Jobs"], ["scheme", "Schemes & Benefits"], ["guide", "Eligibility & Application Guides"]];
export const SCHEME_CATS = ["Employment and skill development", "Education and scholarship", "Assistive-device support", "Financial assistance", "Entrepreneurship support", "Disability welfare", "State-specific benefit"];

export function Disclaimer() {
  return (
    <div className="flex gap-3 rounded-lg border-2 border-[#E8B66B] bg-[#FFF4E5] p-4 text-[#5C3100]" role="note" data-testid="gov-disclaimer">
      <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
      <p><strong>Always check the official notification.</strong> OpenPath summarises public information to make it easier to find. Final eligibility, reservation, age relaxation, deadlines and application requirements are set by the issuing authority. Records marked SAMPLE are not real.</p>
    </div>
  );
}

export const PWD_LABELS = [
  "PwD Eligibility Confirmed",
  "PwD-Reserved Vacancy",
  "Disability-Inclusive Employer",
  "Eligibility Requires Verification",
];

export function GovList() {
  const L = useListing("/gov", { kind: "job" });
  const { params, set } = L;
  const kind = params.kind || "job";
  const chips = ["q", "state", "level", "category", "qualification", "employment_type", "deadline_after", "pwd_eligibility"].filter((k) => params[k]).map((k) => ({ key: k, label: `${label(k === "q" ? "keyword" : k === "deadline_after" ? "deadline after" : k === "pwd_eligibility" ? "PwD label" : k)}: ${k === "deadline_after" ? fmtDate(params[k]) : params[k]}` }));
  return (
    <>
      <PageHeader eyebrow="Government Opportunities" title="Government jobs, schemes & guides" description="Recruitment notices, schemes and benefits relevant to persons with disabilities — each with its official source and last-verified date." />
      <div className="container-x pt-6 space-y-6">
        <Disclaimer />
        <Tabs value={kind} onValueChange={(v) => L.reset({ kind: v })}>
          <TabsList className="h-auto flex-wrap gap-1 bg-mist p-1" aria-label="Government opportunity type">
            {KINDS.map(([v, l]) => <TabsTrigger key={v} value={v} className="min-h-[44px] px-4 text-base font-semibold data-[state=active]:bg-white data-[state=active]:text-navy text-ink" data-testid={`gov-tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>
      <ListingShell L={L} chips={chips} keep={["kind"]} saveKind={`gov_${kind}`} noun={kind === "job" ? "vacancies" : kind === "scheme" ? "schemes" : "guides"}
        searchPlaceholder={kind === "scheme" ? "Scheme name, benefit or authority" : "Post, department or keyword"}
        renderItem={(g) => <GovCard g={g} />} emptyTitle="Nothing found" emptyBody="No verified listings match yet. Administrators add records from official sources as they are reviewed."
        filters={<>
          <FilterSelect k="level" label="Level" options={[["central", "Central government"], ["state", "State government"], ["psu", "Public sector"]]} params={params} set={set} />
          <FilterSelect k="state" label="State" options={STATES} params={params} set={set} />
          {kind === "scheme" && <FilterSelect k="category" label="Scheme type" options={SCHEME_CATS} params={params} set={set} />}
          {kind === "job" && <>
            <FilterSelect k="qualification" label="Qualification" options={["10th", "12th", "Diploma", "Graduate", "Postgraduate"]} params={params} set={set} />
            <FilterSelect k="employment_type" label="Employment type" options={[["permanent", "Permanent"], ["contract", "Contract"], ["apprenticeship", "Apprenticeship"]]} params={params} set={set} />
            <FilterSelect k="pwd_eligibility" label="PwD eligibility label" options={PWD_LABELS} params={params} set={set} />
            <div className="space-y-1.5"><label htmlFor="filter-deadline" className="block font-semibold">Deadline on or after <span className="text-sm font-normal text-sub">(optional)</span></label>
              <input id="filter-deadline" type="date" className="field" value={params.deadline_after || ""} onChange={(e) => set("deadline_after", e.target.value)} data-testid="filter-deadline-input" /></div>
          </>}
        </>} />
      {kind !== "guide" && <div className="container-x pb-10"><OtherOfficialSources kind={kind === "scheme" ? "scheme" : "gov_job"} /></div>}
    </>
  );
}

export function GovDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch(`/gov/${id}`);
  if (loading) return <div className="container-x"><Loading /></div>;
  if (error) return <div className="container-x py-10"><ErrorBox message={error} onRetry={reload} /></div>;
  const g = data.item;
  return (
    <div className="container-x py-8 max-w-4xl">
      <Link to={`/government?kind=${g.kind}`} className="link inline-flex min-h-[44px] items-center gap-1"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to government opportunities</Link>
      <article className="mt-4" data-testid="gov-detail">
        <div className="flex flex-wrap gap-2">
          {g.is_sample && <SampleBadge />}
          <Badge>{KINDS.find((k) => k[0] === g.kind)?.[1]}</Badge>
          {g.level && <Badge tone="grey">{label(g.level)}</Badge>}
          {g.pwd_eligibility_label && (
            <Badge tone="grey" testid="gov-pwd-eligibility">
              {g.pwd_eligibility_label}
            </Badge>
          )}
        </div>
        <h1 className="mt-3 text-3xl md:text-4xl font-extrabold">{g.title}</h1>
        <p className="mt-2 text-lg">{g.authority}</p>
        <div className="mt-5 card p-5 bg-mist" data-testid="gov-source-box">
          <dl className="grid gap-3 sm:grid-cols-3">
            <div><dt className="text-sm font-semibold text-sub">Official source</dt><dd className="font-medium">{g.source_name || "Not yet provided"}</dd></div>
            <div><dt className="text-sm font-semibold text-sub">Last verified</dt><dd className="font-medium" data-testid="gov-last-verified">{g.last_verified ? fmtDate(g.last_verified) : "Not yet verified"}</dd></div>
            <div><dt className="text-sm font-semibold text-sub">Applies to</dt><dd className="font-medium">{g.state || (g.level === "central" ? "All India" : "See notification")}</dd></div>
          </dl>
          <div className="mt-4 flex flex-wrap gap-3">
            {g.notification_url && <a href={g.notification_url} target="_blank" rel="noopener noreferrer" className={btn("secondary")} data-testid="gov-notification-link">Read official notification<ExternalLink className="h-4 w-4" aria-hidden="true" /><span className="sr-only">(opens in new tab)</span></a>}
            {g.official_url ? <a href={g.official_url} target="_blank" rel="noopener noreferrer" className={btn()} data-testid="gov-official-link">{g.kind === "job" ? "Apply on official portal" : "Open official source"}<ExternalLink className="h-4 w-4" aria-hidden="true" /><span className="sr-only">(opens in new tab)</span></a> : <p className="text-sub">No official link has been added for this record.</p>}
          </div>
        </div>
        <div className="mt-6"><Disclaimer /></div>
        <DetailSection title={g.kind === "scheme" ? "Purpose" : "Summary"}>{g.summary}</DetailSection>
        <DetailSection title="Simple English guide">{g.simple_english_summary}</DetailSection>
        <DetailSection title="Eligibility">{g.eligibility}</DetailSection>
        <DetailSection title="PwD / PwBD condition (as stated in the notification)">{g.pwd_eligibility_evidence}</DetailSection>
        <DetailSection title="Disability categories / eligibility conditions (as stated in the notification)">{g.disability_categories}</DetailSection>
        <DetailSection title="Educational qualifications">{g.qualification}</DetailSection>
        <DetailSection title="Age limit and relaxations">{g.age_limit}</DetailSection>
        <DetailSection title="Reservation details">{g.reservation}</DetailSection>
        <DetailSection title="Benefits (as stated in the source)">{g.benefits}</DetailSection>
        <DetailSection title="Required documents">{g.documents}</DetailSection>
        <DetailSection title="How to apply">{g.application_steps}</DetailSection>
        <DetailSection title="Application deadline">{g.deadline && fmtDate(g.deadline)}</DetailSection>
        <DetailSection title="Important caveats">{g.caveats}</DetailSection>
        <div className="mt-8 flex flex-wrap gap-2"><SaveButton kind="gov" id={g.id} initial={data.saved} /><ReportButton type="gov" id={g.id} /></div>
      </article>
    </div>
  );
}
